import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GOAL_TITLE, type Goal, type User } from '@jungles/shared';
import { api } from '../lib/api';
import { useSession, useUser } from '../app/session';
import { useToast } from '../app/toast';
import { BackLink, Header, Screen, TabBar } from '../components/Chrome';
import { Dialog } from '../components/Sheet';

/** P2–P3 · Профиль (SPEC §5). */

const GOALS: Goal[] = ['maintain', 'mass', 'cut'];

export function ProfileScreen() {
  const user = useUser();
  const { updateUser, logout } = useSession();
  const navigate = useNavigate();
  const toast = useToast();

  const [name, setName] = useState(user.name);
  const [goalDays, setGoalDays] = useState(String(user.weeklyWorkoutGoal));
  const [goal, setGoal] = useState<Goal>(user.goal);
  const [timezone, setTimezone] = useState(user.timezone);
  const [busy, setBusy] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  const save = async () => {
    const weeklyWorkoutGoal = Number(goalDays);
    if (!Number.isInteger(weeklyWorkoutGoal) || weeklyWorkoutGoal < 1 || weeklyWorkoutGoal > 14) {
      toast('Цель — от 1 до 14 тренировок в неделю');
      return;
    }
    setBusy(true);
    try {
      const next = await api.patch<User>('/api/me', {
        name: name.trim(),
        weeklyWorkoutGoal,
        goal,
        timezone,
      });
      updateUser(next);
      toast('Настройки сохранены');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  const toggleTheme = async () => {
    const theme = user.theme === 'dark' ? 'light' : 'dark';
    try {
      const next = await api.patch<User>('/api/me', { theme });
      updateUser(next);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Не удалось сменить тему');
    }
  };

  const restartOnboarding = async () => {
    try {
      const next = await api.post<User>('/api/me/onboarding/reset');
      updateUser(next);
      navigate('/onboarding');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Не удалось открыть знакомство');
    }
  };

  return (
    <Screen tabs>
      <Header showAvatar={false} />
      <BackLink to="/" label="Главная" />
      <h1>Ваш ритм</h1>
      <p className="mu" style={{ marginTop: 0 }}>
        {user.telegramId?.startsWith('dev:')
          ? 'Локальный аккаунт разработки'
          : 'Аккаунт Telegram'}
      </p>

      <section className="card">
        <h2>Личные настройки</h2>

        <label className="fl">
          Имя
          <input
            className="f"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={64}
          />
        </label>

        <label className="fl">
          Активных дней в неделю
          <input
            className="f"
            type="number"
            inputMode="numeric"
            min={1}
            max={14}
            value={goalDays}
            onChange={(event) => setGoalDays(event.target.value)}
          />
        </label>

        <label className="fl">
          Цель
          <select
            className="f"
            value={goal}
            onChange={(event) => setGoal(event.target.value as Goal)}
          >
            {GOALS.map((item) => (
              <option key={item} value={item}>
                {GOAL_TITLE[item]}
              </option>
            ))}
          </select>
        </label>

        <label className="fl">
          Часовой пояс
          <input
            className="f"
            value={timezone}
            onChange={(event) => setTimezone(event.target.value)}
            placeholder="Europe/Moscow"
          />
        </label>
        <p className="nt">
          По поясу считаются границы дня и недели: очки, серии и лиги. Меняйте, только если
          переехали.
        </p>

        <button className="b p wide" type="button" onClick={() => void save()} disabled={busy}>
          Сохранить настройки
        </button>
      </section>

      <section className="card">
        <h2>Под вашим контролем</h2>
        <div className="stack">
          <button className="b" type="button" onClick={() => navigate('/profile/privacy')}>
            Приватность и участие
          </button>
          <button className="b" type="button" onClick={() => void toggleTheme()}>
            {user.theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
          </button>
          <button className="b" type="button" onClick={() => void restartOnboarding()}>
            Пройти знакомство ещё раз
          </button>
          <button className="b d" type="button" onClick={() => setConfirmLogout(true)}>
            Выйти
          </button>
        </div>
        <p className="nt">
          Тема хранится в профиле и применяется на всех устройствах. Записи привязаны к аккаунту
          Telegram.
        </p>
      </section>

      <TabBar />

      {confirmLogout ? (
        <Dialog
          title="Выйти из аккаунта?"
          confirmLabel="Выйти"
          danger
          onCancel={() => setConfirmLogout(false)}
          onConfirm={() => {
            logout();
            navigate('/', { replace: true });
          }}
        >
          <p>
            Дневник будет скрыт на этом устройстве. Данные останутся на сервере — вход через
            Telegram вернёт их.
          </p>
        </Dialog>
      ) : null}
    </Screen>
  );
}
